import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, DataTable, Modal, TextInput, Checkbox, SectionCard, useToast, formatDate, type Column,
} from '@datahub/design-system'
import { get, post } from '../../lib/api'
import { ListPage } from '../AdminShell'
import { StatusTag } from '../../components/StatusTag'
import type { AdminUser } from '../../lib/types'

interface AdminRow {
  id: string
  email: string
  displayName: string | null
  status: string
  isBootstrap: boolean
  canInvite: boolean
  invitedBy: { displayName: string | null; email: string } | null
  invitedAt: string | null
  lastSignInAt: string | null
  lockedUntil: string | null
}

/** ADM-08: admin management. There is no sign-up; every admin arrives by invitation. */
export function Settings({ admin }: { admin: AdminUser }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [invite, setInvite] = useState({ email: '', displayName: '', canInvite: false })

  const { data } = useQuery({ queryKey: ['admins'], queryFn: () => get<{ admins: AdminRow[] }>('/admin/settings/admins') })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admins'] })

  const action = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => post(path, body),
    onSuccess: () => { toast.success('Done'); refresh() },
    onError: (e: Error) => toast.error(e.message),
  })

  const sendInvite = useMutation({
    mutationFn: () => post('/admin/settings/admins', invite),
    onSuccess: () => {
      toast.success(`Invitation sent to ${invite.email}`)
      setInviteOpen(false)
      setInvite({ email: '', displayName: '', canInvite: false })
      refresh()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const columns: Column<AdminRow>[] = [
    { key: 'name', header: 'Name', width: 200, sticky: true, render: (a) => a.displayName ?? '—' },
    { key: 'email', header: 'Email', width: 260, render: (a) => a.email },
    { key: 'status', header: 'Status', width: 140, render: (a) => <StatusTag status={a.status} /> },
    { key: 'invite', header: 'Can invite', width: 110, render: (a) => (a.canInvite ? 'Yes' : '-') },
    { key: 'invitedBy', header: 'Invited by', width: 200, render: (a) => a.invitedBy?.displayName ?? a.invitedBy?.email ?? (a.isBootstrap ? 'Environment' : '-') },
    { key: 'lastSignIn', header: 'Last sign-in', width: 150, render: (a) => formatDate(a.lastSignInAt) },
    {
      key: 'actions', header: '', width: 320,
      render: (a) => (
        <span className="flex items-center flex-wrap" style={{ gap: 12 }}>
          {a.status === 'invited' && (
            <>
              <ActionLink label="Resend" onClick={() => action.mutate({ path: `/admin/settings/admins/${a.id}/resend` })} />
              <ActionLink label="Revoke" onClick={() => action.mutate({ path: `/admin/settings/admins/${a.id}/revoke` })} muted />
            </>
          )}
          {a.status === 'active' && (
            <>
              <ActionLink label="Send reset" onClick={() => action.mutate({ path: `/admin/settings/admins/${a.id}/send-reset` })} />
              <ActionLink label="Deactivate" onClick={() => action.mutate({ path: `/admin/settings/admins/${a.id}/deactivate` })} muted />
            </>
          )}
          {a.status === 'deactivated' && (
            <ActionLink label="Reactivate" onClick={() => action.mutate({ path: `/admin/settings/admins/${a.id}/reactivate` })} />
          )}
          {admin.isBootstrap && !a.isBootstrap && (
            <ActionLink
              label={a.canInvite ? 'Remove invite right' : 'Grant invite right'}
              onClick={() => action.mutate({ path: `/admin/settings/admins/${a.id}/invite-right`, body: { canInvite: !a.canInvite } })}
              muted
            />
          )}
        </span>
      ),
    },
  ]

  return (
    <ListPage
      title="Settings"
      actions={admin.canInvite ? <Button variant="filled-grey" size="medium" onClick={() => setInviteOpen(true)}>Invite admin</Button> : undefined}
    >
      <div className="flex-1 overflow-y-auto bg-background-main" style={{ padding: 20 }}>
        <div className="flex flex-col" style={{ gap: 20 }}>
          <SectionCard title="Admins">
            <DataTable columns={columns} rows={data?.admins ?? []} rowKey={(a) => a.id} caption="Admin users" emptyMessage="No admins yet" />
          </SectionCard>

          <SectionCard title="How admin access works">
            <div className="flex flex-col" style={{ padding: '12px 20px', gap: 8 }}>
              {[
                'There is no sign-up. Every admin arrives by invitation from an admin who holds the invite right.',
                'An invitation link is single-use and expires after 7 days. The invitee sets their own password.',
                'A password reset link expires after 60 minutes and ends every other session for that admin.',
                'The default admin comes from ADMIN_USER and ADMIN_PASSWORD, and is created only when no admin exists.',
              ].map((line) => (
                <p key={line} className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px' }}>{line}</p>
              ))}
              {admin.isBootstrap && (
                <div style={{ paddingTop: 8 }}>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => action.mutate({ path: '/admin/settings/bootstrap-admin/reset-password' })}
                  >
                    Reset default admin password from the environment
                  </Button>
                </div>
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite an admin"
        primaryLabel="Send invitation"
        primaryDisabled={!/.+@.+\..+/.test(invite.email) || invite.displayName.trim() === ''}
        primaryLoading={sendInvite.isPending}
        onPrimary={() => sendInvite.mutate()}
      >
        <div className="flex flex-col" style={{ gap: 20 }}>
          <TextInput label="Email" size="large" inputType="email" value={invite.email} onChange={(v) => setInvite({ ...invite, email: v })} />
          <TextInput label="Name" size="large" value={invite.displayName} onChange={(v) => setInvite({ ...invite, displayName: v })} />
          <Checkbox checked={invite.canInvite} onChange={(v) => setInvite({ ...invite, canInvite: v })} label="Allow this admin to invite others" />
          <p className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '16px' }}>
            They receive a single-use link, valid for 7 days, and choose their own password.
          </p>
        </div>
      </Modal>
    </ListPage>
  )
}

function ActionLink({ label, onClick, muted }: { label: string; onClick: () => void; muted?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={muted ? 'text-text-support hover:text-white' : 'text-text-hyperlink hover:underline'}
      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}
    >
      {label}
    </button>
  )
}
